// `grid new` — the front door. Sets up a folder to build an app or agent:
// scaffolds `cloudgrid.yaml`, service starter files, AGENTS.md/CLAUDE.md, and a
// `.gitignore` — then stops. It mints NOTHING on the server and writes NO
// `.cloudgrid/link.json`.
//
// Mint-at-plug (Decision 012, amended 2026-07-18 — "mint at first
// substrate-touching verb"): the server-side entity is created on the FIRST
// substrate-touching verb — `grid dev` OR `grid plug` — whichever runs first,
// via the adopt-yaml resolver. `grid new` is the scaffold-without-mint front
// door; the folder it leaves is a valid, unlinked project.
//
// `grid init` is a hidden alias of `grid new` (muscle memory preserved, not
// taught). The `service` subcommand (`grid new service` / `grid init service`)
// keeps its home here.
//
// Two-question interview (panel decision 2026-07-18): one classify question
// ("what are you building?") + one batched needs-confirm + a skippable name.
// Non-interactive (`--yes`, no TTY) accepts inferred defaults with no prompts.

import { Command } from 'commander';
import { existsSync, mkdirSync, readFileSync, readdirSync, writeFileSync } from 'fs';
import { basename, join, relative, resolve } from 'path';

import { CloudGridError } from '../utils.js';
import { V2ApiError } from '../api/v2-errors.js';
import { handleV2Error } from '../handle-error.js';
import { addGridOption, gridSlug, type GridOpts } from '../grid-opt.js';
import { Output } from '../output.js';
import { addOutputOption, isStructuredOutput } from '../services/output-format.js';
import { readLink } from '../services/link.js';
import { appendGitignoreLine } from '../services/link.js';
import { writeGeneratedYaml } from '../services/generated-yaml.js';
import {
  detectFramework as defaultDetectFramework,
  type FrameworkDetection,
  type DetectedType,
} from '../services/framework-detect.js';
import { inferNeeds as defaultInferNeeds, type SubstrateNeed } from '../services/gridify-needs.js';
import { scanFolder, readRepoSnapshot } from '../../../shared/src/drop-detection/folder-scan.js';
import { runGridify, type GridifyDeps } from '../services/gridify.js';
import { scaffoldService } from './scaffold/scaffolder.js';
import { setExposeFalse } from './scaffold/yaml-edit.js';
import type { ServiceType } from './scaffold/templates.js';
import { AGENT_BOOTSTRAP_BLOCK, mergeManagedBlock, ensureClaudeImport } from '../lib/agent-files.js';
import { initServiceCommand } from './scaffold/service.js';
import { SLUG_MAX, isValidSlug, suggestSlug } from './slug.js';

export { suggestSlug } from './slug.js';

// The service types `grid new` can scaffold a starter for. `cron` is excluded
// (it needs a `--schedule` and rarely stands alone as the first service).
const NEW_TYPES = ['node', 'nextjs', 'python', 'static'] as const;
type NewType = (typeof NEW_TYPES)[number];

// The `needs:` words `grid new` accepts on `--needs`. Mirrors the annotated
// template's need vocabulary. `object_storage` is intentionally excluded (it is
// rejected at plug time, #1678).
const NEED_WORDS = ['database', 'cache', 'kv', 'queue', 'pubsub', 'vector', 'ai', 'disk'] as const;

const DEFAULT_SERVICE_NAME = 'web';

export interface NewOpts extends GridOpts {
  /** Entity slug. Skippable in the interview (defaults to the folder name). */
  name?: string;
  /** Skip classification: node / nextjs / python / static. */
  type?: string;
  /** Pre-declare resources (comma-separated need words); skips the needs question. */
  needs?: string;
  /** Scaffold with an `agent:` block (app-is-an-agent flip, not a separate type). */
  agent?: boolean;
  /** Target directory (default: current folder). */
  dir?: string;
  /** Non-interactive: accept all inferred defaults, no prompts. For scripts / MCP. */
  yes?: boolean;
  /** Skip the AGENTS.md / CLAUDE.md managed block. */
  noAgents?: boolean;
  /** Mark the entity internal-only (`expose: false`). */
  internal?: boolean;
  json?: boolean;
  output?: string;
}

export interface NewDeps {
  /** Injectable framework sniffer (tests pass a stub). */
  detect?: (dir: string) => FrameworkDetection | null;
  /** Injectable needs inference. Defaults to a folder-scan → inferNeeds. */
  inferNeeds?: (dir: string) => SubstrateNeed[];
  /** Injectable free-text classify prompt (Q1). */
  promptText?: (message: string) => Promise<string>;
  /** Injectable yes/no needs confirm (Q2). */
  confirmNeeds?: (message: string, defaultValue?: boolean) => Promise<boolean>;
  /** Injectable slug prompt (Q3, skippable). */
  promptSlug?: (message: string, defaultValue: string) => Promise<string>;
  /** Override TTY detection (tests force a value). */
  isTTY?: boolean;
  /** Output sink. Defaults to stdout. */
  writer?: (s: string) => void;
  /** Injectable gridify (existing-code path). Defaults to {@link runGridify}. */
  gridify?: typeof runGridify;
  /** Deps forwarded to gridify on the existing-code path. */
  gridifyDeps?: GridifyDeps;
}

/**
 * Keyword-classify a free-text "what are you building?" answer into a service
 * type. Deliberately simple and legible — the receipt shows the guess and the
 * user edits the yaml if it is wrong. Also flags an agent intent.
 */
export function classifyDescription(text: string): { type: NewType; agent: boolean } {
  const t = text.toLowerCase();
  const agent = /\bagent\b|\bbot\b|\bchat( ?bot)?\b|\bassistant\b/.test(t);
  let type: NewType = 'node';
  if (/\bnext(\.?js)?\b/.test(t)) type = 'nextjs';
  else if (/\bpython\b|\bflask\b|\bdjango\b|\bfastapi\b/.test(t)) type = 'python';
  else if (/\bstatic\b|\blanding\b|\bhtml\b|\bmarketing site\b/.test(t)) type = 'static';
  return { type, agent };
}

function defaultScanNeeds(dir: string): SubstrateNeed[] {
  try {
    return defaultInferNeeds(readRepoSnapshot(dir, scanFolder(dir)));
  } catch {
    return [];
  }
}

/** Canonical env var a need injects, for the stack receipt (annotated template). */
const NEED_ENV: Record<string, string> = {
  database: 'DATABASE_MONGODB_URL',
  cache: 'CACHE_REDIS_URL',
  kv: 'KV_REDIS_URL',
  queue: 'QUEUE_REDIS_URL',
  pubsub: 'PUBSUB_REDIS_URL',
  vector: 'VECTOR_PGVECTOR_URL',
  ai: 'RUNTIME_GATEWAY_URL',
  disk: 'DISK_PATH',
};

/**
 * Inner runner — exported so tests exercise `grid new` without commander.
 * Scaffolds files only; never mints, never writes a link.
 */
export async function runNew(opts: NewOpts, deps: NewDeps = {}): Promise<void> {
  const writer = deps.writer ?? ((s: string) => process.stdout.write(s));
  const isTTY = deps.isTTY ?? Boolean(process.stdout.isTTY && process.stdin.isTTY);
  const interactive = isTTY && !opts.yes;
  const detect = deps.detect ?? defaultDetectFramework;
  const inferNeeds = deps.inferNeeds ?? defaultScanNeeds;

  const baseCwd = process.cwd();
  let targetDir = opts.dir ? resolve(baseCwd, opts.dir) : baseCwd;
  if (opts.dir && !existsSync(targetDir)) mkdirSync(targetDir, { recursive: true });

  // Never scaffold over an existing project. A yaml-present folder is either
  // already linked (run plug/dev) or set up and ready to mint (run plug/dev).
  if (existsSync(join(targetDir, 'cloudgrid.yaml'))) {
    if (readLink(targetDir) !== null) {
      throw new CloudGridError(
        [
          'This folder already has a cloudgrid.yaml and is linked.',
          'Run grid plug to deploy it, or grid dev to run it locally.',
        ].join('\n'),
      );
    }
    throw new CloudGridError(
      [
        'cloudgrid.yaml already exists here — the folder is set up.',
        'Run grid dev to run it locally, or grid plug to ship it (either one mints the entity).',
      ].join('\n'),
    );
  }

  // Validate `--type` early.
  let forcedType: NewType | null = null;
  if (opts.type !== undefined) {
    if (!NEW_TYPES.includes(opts.type as NewType)) {
      throw new CloudGridError(
        `--type must be one of: ${NEW_TYPES.join(', ')}. Got: '${opts.type}'.`,
      );
    }
    forcedType = opts.type as NewType;
  }

  const detection = detect(targetDir);
  const forcedGreenfield = forcedType !== null || opts.agent === true;

  // Existing-code path: a recognisable app already lives here and the user did
  // NOT force a fresh template → gridify it (write a reviewed yaml, no starter
  // files, no mint). Reuses the exact gridify flow `grid init` used to run.
  if (detection && !forcedGreenfield) {
    const gridify = deps.gridify ?? runGridify;
    const result = await gridify(
      targetDir,
      // A typed `grid new <name>` argument wins over gridify's basename-derived
      // slug — a passed name must never be silently discarded (FIX 3).
      { auto: opts.yes === true, ...(opts.name !== undefined ? { slug: opts.name } : {}) },
      { isTTY, writer, ...(deps.gridifyDeps ?? {}) },
    );
    if (result.kind === 'undetected') {
      // Detection disagreed on the second look — fall through to greenfield.
    } else {
      if (result.kind === 'created') {
        writer(
          [
            '',
            'Next',
            '  grid dev     run it locally with your grid resources tunneled in',
            '  grid plug    build, deploy, and go live',
            '',
          ].join('\n') + '\n',
        );
      }
      return;
    }
  }

  // ── Greenfield scaffold ────────────────────────────────────────────────────

  // Subdir convention (spec §3.3): `grid new <name>` in a non-empty folder
  // scaffolds into `<name>/` — the cargo new / create-next-app convention — so
  // starter files never scatter into an occupied directory. In-place is
  // reserved for an empty folder or a bare `grid new` (no name). An explicit
  // `--dir` already fixed the target, so leave it alone.
  if (opts.name !== undefined && !opts.dir && folderNonEmpty(targetDir)) {
    const sub = resolveSlugOrThrow(opts.name);
    targetDir = join(targetDir, sub);
    if (existsSync(join(targetDir, 'cloudgrid.yaml'))) {
      throw new CloudGridError(
        [
          `${sub}/ already has a cloudgrid.yaml — it looks set up already.`,
          'Run grid dev or grid plug from inside it, or pick another name.',
        ].join('\n'),
      );
    }
    if (!existsSync(targetDir)) mkdirSync(targetDir, { recursive: true });
  }

  // Q1 — classify (what are you building?). Skipped when `--type`/`--agent`
  // already fix the shape, or non-interactively (default node).
  let type: NewType = forcedType ?? 'node';
  let agent = opts.agent === true;
  if (forcedType === null && interactive) {
    const promptText =
      deps.promptText ??
      (async (message: string) => {
        const inquirer = (await import('inquirer')).default;
        const ans = await inquirer.prompt([{ type: 'input', name: 'desc', message }]);
        return String(ans.desc ?? '');
      });
    const desc = (await promptText('What are you building?')).trim();
    if (desc) {
      const classified = classifyDescription(desc);
      type = classified.type;
      agent = agent || classified.agent;
    }
  }

  // Q2 — needs (batched confirm). `--needs` wins; otherwise infer and confirm.
  let needs: string[];
  if (opts.needs !== undefined) {
    needs = parseNeeds(opts.needs);
  } else {
    const inferred = inferNeeds(targetDir);
    if (interactive) {
      const confirmNeeds =
        deps.confirmNeeds ??
        (async (message: string, defaultValue = true) => {
          const inquirer = (await import('inquirer')).default;
          const ans = await inquirer.prompt([
            { type: 'confirm', name: 'ok', message, default: defaultValue },
          ]);
          return Boolean(ans.ok);
        });
      if (inferred.length > 0) {
        const ok = await confirmNeeds(`Looks like you'll want ${inferred.join(' + ')}. Add them?`, true);
        needs = ok ? inferred : [];
      } else {
        const ok = await confirmNeeds('Add a database (Mongo) now?', false);
        needs = ok ? ['database'] : [];
      }
    } else {
      // Non-interactive: accept the inferred defaults (empty on a blank folder).
      needs = inferred;
    }
  }

  // Q3 — name (skippable; defaults to the folder name).
  const dirSlug = suggestSlug(basename(targetDir)) ?? 'my-app';
  let slug: string;
  if (opts.name !== undefined) {
    slug = resolveSlugOrThrow(opts.name);
  } else if (interactive) {
    const promptSlug =
      deps.promptSlug ??
      (async (message: string, defaultValue: string) => {
        const inquirer = (await import('inquirer')).default;
        const ans = await inquirer.prompt([
          { type: 'input', name: 'slug', message, default: defaultValue },
        ]);
        return String(ans.slug ?? '');
      });
    const answer = (await promptSlug('Name it', dirSlug)).trim();
    slug = resolveSlugOrThrow(answer || dirSlug);
  } else {
    slug = resolveSlugOrThrow(dirSlug);
  }

  // ── Write files (no mint, no link.json) ────────────────────────────────────
  writeGeneratedYaml(targetDir, {
    name: slug,
    type: type as DetectedType,
    ...(needs.length > 0 ? { needs } : {}),
    ...(agent ? { agent: true } : {}),
    generatedBy: 'new',
    sourceFromRoot: false,
  });
  if (opts.internal) setExposeFalse(targetDir);

  const scaffold = scaffoldService(targetDir, DEFAULT_SERVICE_NAME, type as ServiceType);

  appendGitignoreLine('.cloudgrid/', targetDir);
  appendGitignoreLine('.cloudgrid-dev.lock', targetDir);
  appendGitignoreLine('.env.local', targetDir);

  let wroteAgents = false;
  if (!opts.noAgents) {
    const agentsPath = join(targetDir, 'AGENTS.md');
    if (!existsSync(agentsPath)) {
      writeFileSync(agentsPath, mergeManagedBlock('', AGENT_BOOTSTRAP_BLOCK).content, 'utf-8');
      const claudePath = join(targetDir, 'CLAUDE.md');
      const claude = existsSync(claudePath) ? readFileSync(claudePath, 'utf-8') : null;
      writeFileSync(claudePath, ensureClaudeImport(claude).content, 'utf-8');
      wroteAgents = true;
    }
  }

  // ── Receipt ────────────────────────────────────────────────────────────────
  const serviceFolder = `services/${DEFAULT_SERVICE_NAME}/`;
  const out = Output.fromOpts({ json: !!opts.json, quiet: false, output: opts.output });
  out.json(() => ({
    name: slug,
    type,
    agent,
    needs,
    dir: targetDir === baseCwd ? '.' : relative(baseCwd, targetDir),
    scaffolded: serviceFolder,
    minted: false,
    linked: false,
  }));
  out.human(() => {
    const lines: string[] = [];
    lines.push(`Set up ${slug}${agent ? ' (agent)' : ''} in ${relative(baseCwd, targetDir) || '.'}`);
    lines.push('');
    lines.push('Wrote:');
    lines.push(`  cloudgrid.yaml        one service (web, ${type})${needs.length ? `, needs: [${needs.join(', ')}]` : ''}`);
    lines.push(`  ${serviceFolder.padEnd(20)}starter files`);
    if (wroteAgents) lines.push('  AGENTS.md, CLAUDE.md  agent playbook');
    lines.push('  .gitignore            added .cloudgrid/');
    lines.push('');
    lines.push('Your stack');
    for (const need of needs) {
      const env = NEED_ENV[need] ?? '(injected env var)';
      lines.push(`  ${need.padEnd(8)}-> ${env.padEnd(22)} created on first grid dev or grid plug`);
    }
    lines.push(`  ${'web'.padEnd(8)}-> your service, port injected as PORT`);
    lines.push('');
    lines.push('Next');
    lines.push('  grid dev     run it locally with your grid resources tunneled in');
    lines.push('  grid plug    build, deploy, and go live');
    lines.push('');
    writer(lines.join('\n'));
    void scaffold; // starter file set already reported as `services/web/`
    return undefined;
  });
}

/**
 * Is the folder occupied? Drives the subdir convention (spec §3.3): a name +
 * a non-empty folder → scaffold into `<name>/`. `.git` alone (a freshly
 * initialised repo) still counts as empty so the common `mkdir/git init/new`
 * flow stays in place.
 */
function folderNonEmpty(dir: string): boolean {
  try {
    return readdirSync(dir).some((entry) => entry !== '.git');
  } catch {
    return false;
  }
}

/** Parse + validate the `--needs a,b` list against the known need vocabulary. */
function parseNeeds(raw: string): string[] {
  const items = raw
    .split(',')
    .map((s) => s.trim().toLowerCase())
    .filter((s) => s.length > 0);
  const seen = new Set<string>();
  const out: string[] = [];
  for (const item of items) {
    if (!(NEED_WORDS as readonly string[]).includes(item)) {
      throw new CloudGridError(
        `--needs '${item}' is not a known resource. Known: ${NEED_WORDS.join(', ')}.`,
      );
    }
    if (!seen.has(item)) {
      seen.add(item);
      out.push(item);
    }
  }
  return out;
}

/** Validate a slug, throwing the §6.1 guidance on failure. */
function resolveSlugOrThrow(name: string): string {
  const trimmed = name.trim();
  if (isValidSlug(trimmed) && trimmed.length <= SLUG_MAX) return trimmed;
  const suggestion = suggestSlug(trimmed);
  const lines = ['Slug must be lowercase alphanumeric with hyphens (3-40 chars).'];
  if (suggestion) lines.push(`Try: ${suggestion}`);
  throw new CloudGridError(lines.join('\n'));
}

/**
 * `grid new` command (canonical). `init` is a hidden alias (muscle memory, not
 * taught). Accepts an optional `[name]`, plus a legacy `<kind> <name>` form
 * (`new app my-api`) so old `init app my-api` muscle memory keeps working — the
 * kind maps to `--agent` when it is `agent`, and never mints.
 */
export const newCommand = new Command('new')
  .alias('init')
  .description('Set up a folder to build an app or agent (no server entity until you plug or dev)')
  .argument('[a]', 'Slug — or the legacy kind (app|agent) when a second argument follows')
  .argument('[b]', 'Slug, in the legacy `new <kind> <slug>` form');
addGridOption(newCommand, 'Grid this project will target (recorded on first plug)');
newCommand
  .option('--type <type>', `Scaffold a 'web' service of the given type (${NEW_TYPES.join('|')})`)
  .option('--needs <list>', `Pre-declare resources (comma-separated: ${NEED_WORDS.join(', ')})`)
  .option('--agent', "Scaffold with an 'agent:' block (an app with an agent: block is an agent)")
  .option('--dir <path>', 'Target directory (defaults to the current folder)')
  .option('--yes', 'Non-interactive: accept inferred defaults, no prompts')
  .option('--internal', "Mark the entity internal-only (yaml gets 'expose: false')")
  .option('--no-agents', 'Skip the AGENTS.md / CLAUDE.md managed block')
  .option('--json', 'Machine-readable output (alias of -o json)');
addOutputOption(newCommand);
newCommand.action(async (a: string | undefined, b: string | undefined, opts: NewOpts) => {
  const start = Date.now();

  // Structured mode is non-interactive by contract. The interview has no
  // machine-readable mode; a script must pass `--yes` (and enough flags) to run
  // headless. Fail fast with the envelope instead of hanging on a prompt
  // (matches the #1785 folder-init guard).
  if (isStructuredOutput(opts) && !opts.yes) {
    await handleV2Error(
      new V2ApiError(
        400,
        'STRUCTURED_MODE_UNSUPPORTED',
        'grid new runs an interactive interview, which has no machine-readable mode. Re-run with --yes (and --type/--name as needed) for structured output.',
      ),
      { json: true },
    );
    return;
  }

  // Resolve the positional shape: `new`, `new <name>`, or legacy
  // `new <kind> <name>` (kind ∈ {app, agent}).
  let name: string | undefined;
  let agentFromKind = false;
  if (a !== undefined && b !== undefined && (a === 'app' || a === 'agent')) {
    agentFromKind = a === 'agent';
    name = b;
  } else if (a !== undefined) {
    name = a;
  }

  await runNew({
    ...opts,
    name: name ?? opts.name,
    agent: opts.agent === true || agentFromKind,
    grid: gridSlug(opts) ?? undefined,
  });

  // Decision 016 — best-effort success event on a high-signal verb.
  try {
    const { recordCliEvent } = await import('../services/telemetry.js');
    await recordCliEvent({
      event_type: 'success',
      command: 'grid new',
      elapsed_ms: Date.now() - start,
    });
  } catch { /* swallow */ }
});

// `grid new service <name>` / `grid init service <name>` — add a service to the
// project. Home preserved from the old `init` tree (BL621 Phase 3).
newCommand.addCommand(initServiceCommand);

// Back-compat export: `init` is a hidden alias of `new`, so importers that
// still reference `initCommand` (program registration, command-tree tests) get
// the same object.
export const initCommand = newCommand;
